/**
 * Carrier tracking URLs.
 *
 * Centralised here because it existed twice and only one copy worked:
 * `FacilityPackageComponent.trackingNumberURL()` had the real carrier logic,
 * while `EcommerceOrdersComponent.getTrackingLink()` returned
 * `https://www.example.com/track?id=...` -- a placeholder that shipped, so every
 * tracking number on the ecommerce orders grid was a link to nowhere.
 *
 * The number itself is the most reliable signal, because it is the one piece of
 * data that always travels with the shipment; `carrier` is frequently absent
 * (the EcommerceOrder carries a comma-joined `trackingNumbers` string and no
 * carrier at all). So prefixes are checked first and `carrier` is used only to
 * disambiguate what the prefixes cannot.
 */

/** USPS IMpb numbers are 22 digits beginning `94`. */
const USPS_TRACK = 'https://tools.usps.com/go/TrackConfirmAction_input?origTrackNum=';
/** UPS numbers are `1Z` + 16 alphanumerics. */
const UPS_TRACK = 'http://wwwapps.ups.com/WebTracking/track?track=yes&trackNums=';
const FEDEX_TRACK = 'https://www.fedex.com/apps/fedextrack/?action=track&trackingnumber=';

/**
 * A tracking URL for one tracking number.
 *
 * @param trackingNumber a single number. NOT a comma-joined list -- split first.
 * @param carrier optional hint, e.g. 'FEDEX' / 'UPS' / 'USPS'. Only consulted
 *        when the number itself is not recognisable.
 * @returns the URL, or '' when there is nothing to link to. Callers must treat
 *          '' as "render no link" rather than feeding it to an href, which
 *          would produce a link back to the current page.
 */
export function getTrackingUrl(trackingNumber: string, carrier?: string): string {
  if ( !trackingNumber ) {
    return '';
  }
  // Tracking numbers arrive comma-joined and are often split without trimming,
  // so a leading space is normal rather than exceptional.
  const number = ('' + trackingNumber).trim();
  if ( !number ) {
    return '';
  }
  // Always encoded: these end up in a query string, and a stray space or '#'
  // in a malformed number would otherwise truncate the URL silently.
  const encoded = encodeURIComponent(number);

  if ( number.startsWith('94') ) {
    return USPS_TRACK + encoded;
  }
  if ( number.startsWith('1Z') || number.startsWith('1z') ) {
    return UPS_TRACK + encoded;
  }

  // Prefix unrecognised, so fall back to the carrier if we were given one.
  const carrierName = carrier ? ('' + carrier).trim().toUpperCase() : '';
  if ( carrierName === 'FEDEX' ) {
    return FEDEX_TRACK + encoded;
  }
  if ( carrierName === 'UPS' ) {
    return UPS_TRACK + encoded;
  }

  // Default to USPS. This matches the long-standing behaviour of
  // FacilityPackageComponent: most numbers here are USPS, and USPS's lookup
  // fails gracefully with "not found" rather than erroring.
  return USPS_TRACK + encoded;
}

/**
 * Split a comma-joined `trackingNumbers` string into usable numbers.
 *
 * Tolerates null/undefined, because the server sends this column as a nullable
 * varchar and a bare `.split()` in a template throws where it cannot be caught
 * -- which takes the whole row's rendering with it.
 */
export function splitTrackingNumbers(trackingNumbers: string): string[] {
  if ( !trackingNumbers ) {
    return [];
  }
  return ('' + trackingNumbers)
    .split(',')
    .map(trackingNumber => trackingNumber.trim())
    .filter(trackingNumber => trackingNumber.length > 0);
}

